import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Wallet,
  Calculator,
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WithdrawalPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Data
  const [availableBalance, setAvailableBalance] = useState(0);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [calculation, setCalculation] = useState(null);
  
  // Form
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'JPY',
    method: 'wise',
    bank_name: '',
    account_holder: '',
    account_number: '',
    swift_code: '',
    additional_info: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [dashRes, ratesRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard`, { withCredentials: true }),
        axios.get(`${API_URL}/api/exchange-rates`),
        axios.get(`${API_URL}/api/withdrawals/history`, { withCredentials: true })
      ]);
      setAvailableBalance(dashRes.data.total_assets);
      setExchangeRates(ratesRes.data);
      setWithdrawalHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error(t('withdrawal.inputError'));
      return;
    }
    
    setCalculating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/withdrawals/calculate`,
        {
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          method: formData.method
        },
        { withCredentials: true }
      );
      setCalculation(response.data);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('withdrawal.calcError'));
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.bank_name || !formData.account_holder || !formData.account_number) {
      toast.error(t('withdrawal.bankError'));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/withdrawals/submit`,
        {
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          method: formData.method,
          bank_name: formData.bank_name,
          account_holder: formData.account_holder,
          account_number: formData.account_number,
          swift_code: formData.swift_code || null,
          additional_info: formData.additional_info || null
        },
        { withCredentials: true }
      );
      toast.success(t('withdrawal.success'));
      setStep(3);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('withdrawal.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value, currency = 'JPY') => {
    if (currency === 'JPY') {
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
        maximumFractionDigits: 0
      }).format(value);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
      approved: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
      completed: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
      rejected: 'bg-red-500/10 text-red-500 border-red-500/30'
    };
    const labels = {
      pending: t('withdrawal.statusPending'),
      approved: t('withdrawal.statusApproved'),
      completed: t('withdrawal.statusCompleted'),
      rejected: t('withdrawal.statusRejected')
    };
    return <Badge className={`${styles[status] || styles.pending} border`}>{labels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Skeleton className="h-8 w-48 mb-8 bg-[#141414]" />
          <Skeleton className="h-96 w-full bg-[#141414]" />
        </div>
      </div>
    );
  }

  if (!user?.is_kyc_verified) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="card-presage rounded-sm">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-white font-heading text-2xl mb-2">{t('withdrawal.needKyc')}</h2>
              <p className="text-zinc-500 mb-6">{t('withdrawal.needKycDesc')}</p>
              <Button
                className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                onClick={() => navigate('/kyc')}
              >
                {t('kyc.start')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white mb-6"
          onClick={() => step > 1 && step < 3 ? setStep(step - 1) : navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <Wallet className="h-8 w-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl text-white mb-2">{t('withdrawal.title')}</h1>
          <p className="text-zinc-500">{t('withdrawal.subtitle')}</p>
        </div>

        {/* Available Balance */}
        <Card className="card-presage rounded-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-500 text-sm">{t('withdrawal.available')}</p>
                <p className="text-white font-mono text-3xl">{formatCurrency(availableBalance)}</p>
              </div>
              <Wallet className="h-8 w-8 text-zinc-600" />
            </div>
          </CardContent>
        </Card>

        {/* Step Progress */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s, index) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${step >= s ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-500'}`}>
                {step > s ? <CheckCircle className="h-4 w-4" /> : s}
              </div>
              {index < 2 && (
                <div className={`w-16 h-0.5 mx-2 ${step > s ? 'bg-white' : 'bg-zinc-800'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Amount & Method */}
        {step === 1 && (
          <Card className="card-presage rounded-sm">
            <CardHeader>
              <CardTitle className="text-white">{t('withdrawal.selectAmount')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-zinc-400">{t('withdrawal.amount')}</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="100000"
                  min={10000}
                  max={availableBalance}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 text-lg"
                  data-testid="withdrawal-amount"
                />
                <p className="text-zinc-600 text-xs mt-1">{t('withdrawal.minAmount')}</p>
              </div>

              <div>
                <Label className="text-zinc-400">{t('withdrawal.method')}</Label>
                <Select
                  value={formData.method}
                  onValueChange={(value) => setFormData({ ...formData, method: value })}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1C] border-white/10">
                    <SelectItem value="wise" className="text-zinc-300">{t('withdrawal.wiseOption', { fee: '0.5' })}</SelectItem>
                    <SelectItem value="prc_remit" className="text-zinc-300">{t('withdrawal.prcOption', { fee: '0.8' })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-zinc-400">{t('withdrawal.currency')}</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1C] border-white/10">
                    <SelectItem value="JPY" className="text-zinc-300"><img src="https://flagcdn.com/w20/jp.png" alt="" className="w-4 h-auto inline mr-1 rounded-sm" /> {t('withdrawal.jpyOption')}</SelectItem>
                    <SelectItem value="USD" className="text-zinc-300"><img src="https://flagcdn.com/w20/us.png" alt="" className="w-4 h-auto inline mr-1 rounded-sm" /> {t('withdrawal.usdOption')}</SelectItem>
                    <SelectItem value="BRL" className="text-zinc-300"><img src="https://flagcdn.com/w20/br.png" alt="" className="w-4 h-auto inline mr-1 rounded-sm" /> {t('withdrawal.brlOption')}</SelectItem>
                    <SelectItem value="EUR" className="text-zinc-300"><img src="https://flagcdn.com/w20/eu.png" alt="" className="w-4 h-auto inline mr-1 rounded-sm" /> {t('withdrawal.eurOption')}</SelectItem>
                    <SelectItem value="GBP" className="text-zinc-300"><img src="https://flagcdn.com/w20/gb.png" alt="" className="w-4 h-auto inline mr-1 rounded-sm" /> {t('withdrawal.gbpOption')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm py-6"
                onClick={handleCalculate}
                disabled={calculating || !formData.amount}
                data-testid="calculate-button"
              >
                {calculating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('withdrawal.calculating')}</>
                ) : (
                  <><Calculator className="h-4 w-4 mr-2" />{t('withdrawal.calculate')}</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Confirmation & Bank Details */}
        {step === 2 && calculation && (
          <div className="space-y-6">
            <Card className="card-presage rounded-sm">
              <CardHeader>
                <CardTitle className="text-white">{t('withdrawal.confirm')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-[#0A0A0A] p-4 rounded-sm space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('withdrawal.amountLabel')}</span>
                    <span className="text-white font-mono">{formatCurrency(calculation.requested_amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">{t('withdrawal.feeLabel', { percentage: calculation.fee_percentage })}</span>
                    <span className="text-red-400 font-mono">-{formatCurrency(calculation.withdrawal_fee)}</span>
                  </div>
                  <div className="flex justify-between border-t border-white/10 pt-3">
                    <span className="text-zinc-400">{t('withdrawal.afterFee')}</span>
                    <span className="text-white font-mono">{formatCurrency(calculation.net_amount_jpy)}</span>
                  </div>
                  {calculation.target_currency && calculation.target_currency !== 'JPY' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">{t('withdrawal.exchangeRate')}</span>
                        <span className="text-zinc-400 font-mono">1 JPY = {calculation.exchange_rate} {calculation.target_currency}</span>
                      </div>
                      <div className="flex justify-between bg-emerald-500/10 -mx-4 px-4 py-3 rounded-sm">
                        <span className="text-emerald-400 font-semibold">{t('withdrawal.expectedAmount')}</span>
                        <span className="text-emerald-400 font-mono text-xl">
                          {formatCurrency(calculation.estimated_receive_amount, calculation.target_currency)}
                        </span>
                      </div>
                    </>
                  )}
                  {(!calculation.target_currency || calculation.target_currency === 'JPY') && (
                    <div className="flex justify-between bg-emerald-500/10 -mx-4 px-4 py-3 rounded-sm">
                      <span className="text-emerald-400 font-semibold">{t('withdrawal.expectedAmount')}</span>
                      <span className="text-emerald-400 font-mono text-xl">
                        {formatCurrency(calculation.net_amount_jpy)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="card-presage rounded-sm">
              <CardHeader>
                <CardTitle className="text-white">{t('withdrawal.bankInfo')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-zinc-400">{t('withdrawal.bankName')}</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    placeholder={t('withdrawal.bankNamePlaceholder')}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1"
                    data-testid="bank-name"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">{t('withdrawal.accountName')}</Label>
                  <Input
                    value={formData.account_holder}
                    onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })}
                    placeholder={t('withdrawal.accountNamePlaceholder')}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1"
                    data-testid="account-holder"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">{t('withdrawal.accountNumber')}</Label>
                  <Input
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    placeholder="1234567"
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1"
                    data-testid="account-number"
                  />
                </div>
                {formData.currency !== 'JPY' && (
                  <div>
                    <Label className="text-zinc-400">{t('withdrawal.swiftLabel')}</Label>
                    <Input
                      value={formData.swift_code}
                      onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                      placeholder="BOABORSP"
                      className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-zinc-400">{t('withdrawal.notes')}</Label>
                  <Input
                    value={formData.additional_info}
                    onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                    placeholder={t('withdrawal.notesPlaceholder')}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1"
                  />
                </div>

                <Button
                  className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm py-6 mt-4"
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="submit-withdrawal"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('withdrawal.submitting')}</>
                  ) : (
                    <>{t('withdrawal.submitBtn')}<ArrowRight className="h-4 w-4 ml-2" /></>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Complete */}
        {step === 3 && (
          <Card className="card-presage rounded-sm">
            <CardContent className="p-8 text-center">
              <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-white font-heading text-2xl mb-2">{t('withdrawal.doneTitle')}</h2>
              <p className="text-zinc-500 mb-6">
                {t('withdrawal.completedText')}<br />
                {t('withdrawal.processingText')}
              </p>
              <Button
                className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                onClick={() => navigate('/dashboard')}
              >
                {t('common.back')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Withdrawal History */}
        {withdrawalHistory.length > 0 && step !== 3 && (
          <Card className="card-presage rounded-sm mt-8">
            <CardHeader>
              <CardTitle className="text-white text-lg">{t('withdrawal.history')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {withdrawalHistory.slice(0, 5).map((w) => (
                  <div key={w.id} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-sm">
                    <div>
                      <p className="text-white font-mono">{formatCurrency(w.amount)}</p>
                      <p className="text-zinc-600 text-xs">
                        {new Date(w.created_at).toLocaleDateString('ja-JP')}
                      </p>
                    </div>
                    {getStatusBadge(w.status)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WithdrawalPage;
